import React, { StrictMode, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import initialize from './root/initialize';
import { configure } from './root/store';
import Routes from './pages/Routes';
import GlobalStyle from './components/GlobalStyle';

/**
 * NOTE: During the migration, the router provider needs to be shared so it will
 *       be provided at the original entry. Once we can deprecate old studio, we
 *       can bring the provider back here.
 *
 *       Additionally, this application will accept the legacy application as a
 *       child in order to pass down the initialized bootstrap data for the
 *       legacy application to use if needed. Specifically, having access
 *       feature flags is crucial so that we can properly set up the available
 *       routes for next studio and the passthrough routes for legacy studio.
 */

/**
 * Hook to fetch auth and configure Redux store
 */
const useInitialize = () => {
  const [state, setState] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const bootstrap = await initialize();
        const store = configure(bootstrap);
        setState({ bootstrap, store });
      } catch (error) {
        setState({ error });

        // TODO: Handle error properly...
        // eslint-disable-next-line no-console
        console.error(error);
      }
    })();
  }, []);

  return state;
};

export function NextApplication({ children }) {
  const { bootstrap, error, store } = useInitialize();

  // If the store has not been set or an error occurred, show nothing (for now)
  // to ensure we don't block legacy studio from loading properly. Eventually,
  // we will want to show a loading state and actionable error state.
  if (store == null || error) {
    // FIXME: Remove this behavior once we have pages in studio next
    return null;
  }

  // Otherwise render the application
  return (
    <StrictMode>
      <GlobalStyle />

      <Provider store={store}>
        <Routes />
      </Provider>

      {children?.(bootstrap)}
    </StrictMode>
  );
}

NextApplication.propTypes = {
  children: PropTypes.func,
};
